import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../styles/theme';

const AdminToggle: React.FC = () => {
  const { isAdmin, logout, login } = useAuth();
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    top: theme.spacing.md,
    right: theme.spacing.md,
    zIndex: theme.zIndex.dropdown,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  };

  const badgeStyle: React.CSSProperties = {
    backgroundColor: isAdmin ? theme.colors.accent : theme.colors.primary,
    color: 'white',
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: '9999px', // Full rounded
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.border,
    color: theme.colors.text,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    borderRadius: theme.borderRadius.md,
    fontSize: '0.75rem',
    border: 'none',
    cursor: 'pointer',
    transition: `background-color ${theme.transitions.fast}`,
  };

  const switchButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: isAdmin ? theme.colors.primary : theme.colors.accent,
  };

  const handleSwitchRole = async () => {
    if (!showPasswordPrompt) {
      setShowPasswordPrompt(true);
      return;
    }

    try {
      const success = await login(password);
      if (success) {
        setShowPasswordPrompt(false);
        setPassword('');
        setError('');
      } else {
        setError('Invalid password');
        setPassword('');
      }
    } catch (error) {
      setError('Login failed');
      setPassword('');
    }
  };

  const handleCancel = () => {
    setShowPasswordPrompt(false);
    setPassword('');
    setError('');
  };

  return (
    <div style={containerStyle}>
      <div style={badgeStyle}>
        {isAdmin ? '👑 Admin' : '👁️ Viewer'}
      </div>
      
      {showPasswordPrompt ? (
        <>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            style={{
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.border}`,
              fontSize: '0.75rem',
              width: '120px',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSwitchRole();
              if (e.key === 'Escape') handleCancel();
            }}
            autoFocus
          />
          <button onClick={handleSwitchRole} style={switchButtonStyle}>
            ✓
          </button>
          <button onClick={handleCancel} style={buttonStyle}>
            ✕
          </button>
        </>
      ) : (
        <>
          <button onClick={handleSwitchRole} style={switchButtonStyle}>
            {isAdmin ? '👁️ Viewer' : '👑 Admin'}
          </button>
          <button
            onClick={logout}
            style={buttonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.surface;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.border;
            }}
          >
            Logout
          </button>
        </>
      )}
      
      {error && (
        <div style={{
          color: theme.colors.error,
          fontSize: '0.65rem',
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: theme.spacing.xs,
          whiteSpace: 'nowrap',
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default AdminToggle;